import { useState, useMemo, useCallback, useEffect } from 'react';
import clsx from 'clsx';

import { Card, Badge, Button, Avatar, Icon, Progress } from '@lib/ui';
import { useAuthContext } from '@lib/auth';
import type { WizardPersona } from '../types';
import { generateMockContent, CHANNELS, DECISION_DIMENSIONS, defaultDecisions } from '../types';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8787';

export type PersonaPreviewProps = {
  persona: Partial<WizardPersona>;
  selectedChannel?: string;
  topic?: string;
  language?: string;
  onChannelChange?: (channel: string) => void;
  compact?: boolean;
  className?: string;
  /** Use real AI generation instead of mock templates */
  useRealGeneration?: boolean;
};

interface GeneratedContentState {
  content: string;
  highlights: string[];
  isLoading: boolean;
  error: string | null;
}

function PersonaPreview({
  persona,
  selectedChannel = 'LinkedIn',
  topic,
  language = 'english',
  onChannelChange,
  compact = false,
  className,
  useRealGeneration = false,
}: PersonaPreviewProps) {
  const [refreshKey, setRefreshKey] = useState(0);
  const { getAccessToken } = useAuthContext();

  // State for AI-generated content
  const [aiContent, setAiContent] = useState<GeneratedContentState>({
    content: '',
    highlights: [],
    isLoading: false,
    error: null,
  });

  // Transform persona to API format
  const transformPersonaForApi = useCallback(
    (p: Partial<WizardPersona>) => ({
      name: p.name || 'Unnamed Persona',
      tagline: p.tagline || null,
      worldview: {
        core_beliefs: p.worldview?.coreBeliefs || [],
        values: p.worldview?.values || [],
        audience: p.worldview?.audience || '',
        purpose: p.worldview?.purpose || '',
      },
      voice: {
        traits: p.voice?.traits || [],
        description: p.voice?.description || '',
        example_phrases: p.voice?.examplePhrases || [],
        avoid_phrases: p.voice?.avoidPhrases || [],
      },
      dimensions: {
        claim_ownership: p.decisions?.claimOwnership || 'personal',
        confidence: p.decisions?.confidence || 'steady',
        call_to_action: p.decisions?.callToAction || 'open',
        vulnerability: p.decisions?.vulnerability || 'balanced',
        energy: p.decisions?.energy || 'engaged',
        formality: p.decisions?.formality || 'conversational',
        specificity: p.decisions?.specificity || 'balanced',
      },
    }),
    []
  );

  // Generate content using real AI
  const generateAiContent = useCallback(async () => {
    if (!persona.name) return;

    setAiContent((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const token = await getAccessToken();
      const response = await fetch(`${API_BASE}/personas/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...transformPersonaForApi(persona),
          topic: topic || 'sharing thoughts and insights',
          channel: selectedChannel,
          language,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate content');
      }

      const data = await response.json();
      setAiContent({
        content: data.content,
        highlights: data.highlights || [],
        isLoading: false,
        error: null,
      });
    } catch (err) {
      setAiContent((prev) => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to generate',
      }));
    }
  }, [persona, selectedChannel, topic, language, getAccessToken, transformPersonaForApi]);

  // Auto-generate on mount or when regenerate is triggered (for real generation mode)
  useEffect(() => {
    if (useRealGeneration && persona.name && refreshKey > 0) {
      generateAiContent();
    }
  }, [refreshKey, useRealGeneration, persona.name, generateAiContent]);

  // Generate mock content (fallback or when not using real generation)
  const mockContent = useMemo(() => {
    return generateMockContent({
      persona,
      channel: selectedChannel,
      topic,
    });
  }, [persona, selectedChannel, topic]);

  // Determine which content to show
  const content = useRealGeneration && aiContent.content ? aiContent.content : mockContent.content;
  const highlights =
    useRealGeneration && aiContent.highlights.length > 0
      ? aiContent.highlights
      : mockContent.highlights;
  const isLoading = useRealGeneration && aiContent.isLoading;

  // Get available channels (now just use default channels)
  const availableChannels = CHANNELS.slice(0, 5);

  // Get decision labels for highlights
  const decisions = persona.decisions || defaultDecisions;
  const decisionHighlights = highlights.map((h) => {
    const dimension = DECISION_DIMENSIONS.find((d) => d.key === h);
    if (dimension) {
      const level = dimension.levels.find((l) => l.value === decisions[dimension.key]);
      return level?.label || h;
    }
    return h;
  });

  return (
    <Card skin="bordered" className={clsx('overflow-hidden', className)}>
      {/* Header */}
      <div className="px-4 py-3 bg-gray-50 dark:bg-dark-700 border-b border-gray-200 dark:border-dark-600">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {persona.name && (
              <>
                <Avatar
                  size="xs"
                  className="bg-primary-500 text-white text-xs"
                  name={persona.avatar || persona.name.slice(0, 2).toUpperCase()}
                />
                <span className="font-medium text-sm text-gray-900 dark:text-white">
                  {persona.name}
                </span>
              </>
            )}
            {!persona.name && (
              <span className="text-sm text-gray-500 dark:text-dark-400">Preview</span>
            )}
          </div>

          <Button
            variant="flat"
            onClick={() => {
              if (useRealGeneration) {
                generateAiContent();
              } else {
                setRefreshKey((k) => k + 1);
              }
            }}
            disabled={isLoading}
            className="gap-1 text-xs"
          >
            <Icon name="refresh" size="sm" className={isLoading ? 'animate-spin' : ''} />
            {isLoading ? 'Generating...' : 'Regenerate'}
          </Button>
        </div>

        {/* Channel selector */}
        {!compact && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {availableChannels.map((channel) => (
              <Badge
                key={channel}
                variant={channel === selectedChannel ? 'filled' : 'soft'}
                color={channel === selectedChannel ? 'primary' : 'neutral'}
                className="cursor-pointer transition-colors"
                onClick={() => onChannelChange?.(channel)}
              >
                {channel}
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Content preview */}
      <div className="p-4">
        {/* Channel badge */}
        <div className="flex items-center gap-2 mb-3">
          <Badge variant="outlined" color="info">
            {selectedChannel}
          </Badge>
          {useRealGeneration && (
            <Badge variant="soft" color="success" className="text-xs">
              AI Generated
            </Badge>
          )}
        </div>

        {/* Generated content */}
        <div className="prose prose-sm dark:prose-invert max-w-none">
          {isLoading ? (
            <div className="space-y-3 animate-pulse">
              <div className="h-4 bg-gray-200 dark:bg-dark-600 rounded w-full" />
              <div className="h-4 bg-gray-200 dark:bg-dark-600 rounded w-5/6" />
              <div className="h-4 bg-gray-200 dark:bg-dark-600 rounded w-4/5" />
              <div className="h-4 bg-gray-200 dark:bg-dark-600 rounded w-full" />
              <div className="h-4 bg-gray-200 dark:bg-dark-600 rounded w-3/4" />
            </div>
          ) : aiContent.error && useRealGeneration ? (
            <div className="text-red-500 dark:text-red-400 text-sm">
              <p className="font-medium">Generation failed</p>
              <p className="text-xs mt-1">{aiContent.error}</p>
              <p className="text-xs mt-2 text-gray-500 dark:text-dark-400">
                Showing template preview instead:
              </p>
              <pre className="whitespace-pre-wrap font-sans text-sm text-gray-700 dark:text-dark-200 bg-transparent p-0 m-0 mt-2">
                {mockContent.content}
              </pre>
            </div>
          ) : (
            <pre className="whitespace-pre-wrap font-sans text-sm text-gray-700 dark:text-dark-200 bg-transparent p-0 m-0">
              {content}
            </pre>
          )}
        </div>

        {/* Highlights - decisions that influenced the output */}
        {decisionHighlights.length > 0 && (
          <div className="mt-4 pt-3 border-t border-gray-100 dark:border-dark-600">
            <p className="text-xs font-medium text-gray-500 dark:text-dark-400 mb-2">
              Influenced by:
            </p>
            <div className="flex flex-wrap gap-1">
              {decisionHighlights.map((highlight, idx) => (
                <Badge key={idx} variant="soft" color="success" className="text-xs">
                  {highlight}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Quality indicator */}
      {!compact && (
        <div className="px-4 py-3 bg-gray-50 dark:bg-dark-700 border-t border-gray-200 dark:border-dark-600">
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500 dark:text-dark-400">Persona completeness</span>
            <div className="flex items-center gap-2">
              <Progress
                value={getCompletenessScore(persona)}
                color="primary"
                className="w-24 h-1.5 rounded-full overflow-hidden"
              />
              <span className="font-medium text-gray-700 dark:text-dark-200">
                {getCompletenessScore(persona)}%
              </span>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

// Calculate persona completeness score based on new model
function getCompletenessScore(persona: Partial<WizardPersona>): number {
  let score = 0;
  const weights = {
    name: 15,
    tagline: 10,
    audience: 10,
    purpose: 10,
    traits: 20,
    description: 15,
    examplePhrases: 10,
    decisions: 10,
  };

  if (persona.name) score += weights.name;
  if (persona.tagline) score += weights.tagline;
  if (persona.worldview?.audience) score += weights.audience;
  if (persona.worldview?.purpose) score += weights.purpose;
  if (persona.voice?.traits?.length) score += weights.traits;
  if (persona.voice?.description) score += weights.description;
  if (persona.voice?.examplePhrases?.length) score += weights.examplePhrases;
  if (persona.decisions) score += weights.decisions;

  return Math.min(100, score);
}

PersonaPreview.displayName = 'PersonaPreview';

export { PersonaPreview };
